import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
// Collection pages omit PDP form scripts; the lazy-loaded modal needs them too.
import '@theme/product-form';
import '@theme/component-quantity-selector';
import '@theme/variant-picker';

const OPEN_SELECTOR = '[data-open-add-to-bag][data-product-url]';
const PRODUCT_INFORMATION_SELECTOR = '.product-information[id^="ProductInformation-"]';
const PRODUCT_INFORMATION_STYLE_MARKER = '.product-accordion.product-accordion';
const MODAL_CONTENT_ID = 'add-to-bag-modal-content';
const BUDGET_PAY_CORE_FALLBACK =
  'https://cdn.shopify.com/extensions/019f5a0c-bccc-7483-9485-bba809bfaea9/tjc-production-budget-pay-16/assets/bp-cart-core.js';

class AddToBagModal extends DialogComponent {
  static requiredRefs = ['dialog'];

  #cache = new Map();
  #controllers = new Set();
  #eventController = new AbortController();
  #openRequestId = 0;
  #prefetchObserver = null;
  #triggerObserver = null;
  #stickyResizeObserver = null;
  #budgetPayCoreSource = BUDGET_PAY_CORE_FALLBACK;
  #returnCartDrawer = null;
  #cartClosePromise = null;
  #closePromise = null;
  #baseCloseDialog = this.closeDialog;

  closeDialog = () => {
    if (this.#closePromise) return this.#closePromise;
    this.#closePromise = this.#baseCloseDialog().then(() => {
      const drawer = this.#returnCartDrawer;
      this.#returnCartDrawer = null;
      if (this.isConnected && drawer?.isConnected && !drawer.querySelector('dialog')?.open) {
        drawer.showDialog();
      }
    }).finally(() => {
      this.#closePromise = null;
    });
    return this.#closePromise;
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#handleOpenClick, true);
    document.addEventListener('pointerover', this.#handlePrefetchIntent, true);
    document.addEventListener('focusin', this.#handlePrefetchIntent, true);
    document.addEventListener('touchstart', this.#handlePrefetchIntent, { capture: true, passive: true });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    this.addEventListener('click', this.#handleModalClick, true);
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    this.refs.dialog?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#eventController.signal,
    });

    this.#observeTriggers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('click', this.#handleOpenClick, true);
    document.removeEventListener('pointerover', this.#handlePrefetchIntent, true);
    document.removeEventListener('focusin', this.#handlePrefetchIntent, true);
    document.removeEventListener('touchstart', this.#handlePrefetchIntent, true);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    this.removeEventListener('click', this.#handleModalClick, true);
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    this.#eventController.abort();
    this.#eventController = new AbortController();
    this.#prefetchObserver?.disconnect();
    this.#triggerObserver?.disconnect();
    this.#stickyResizeObserver?.disconnect();
    this.#controllers.forEach((controller) => controller.abort());
    this.#controllers.clear();
  }

  close() {
    this.closeDialog();
  }

  #handleCartUpdate = () => {
    if (this.refs.dialog?.open) this.closeDialog();
  };

  #handleQuantityUpdate = (event) => {
    const labelValue = event.target?.closest?.('.product-form-buttons')?.querySelector('[data-modal-quantity-value]');
    if (labelValue) labelValue.textContent = String(event.detail?.quantity ?? 1);
  };

  #handleOpenClick = (event) => {
    const trigger = event.target?.closest?.(OPEN_SELECTOR);
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.openWithProduct(trigger.dataset.productUrl);
  };

  #handlePrefetchIntent = (event) => {
    const trigger = event.target?.closest?.(OPEN_SELECTOR);
    if (trigger instanceof HTMLElement) {
      this.#prefetch(trigger.dataset.productUrl);
    }
  };

  #handleModalClick = (event) => {
    const budgetPayHost = event.target?.closest?.('[data-budget-pay-toggle]');
    const budgetPayButton =
      budgetPayHost?.querySelector?.('button, .budget-pay-toggle__button') ??
      event.target?.closest?.('.budget-pay-toggle__button');
    if (budgetPayHost && budgetPayButton && this.contains(budgetPayHost)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      void this.#handleBudgetPayClick(budgetPayButton);
      return;
    }

    const readMoreButton = event.target?.closest?.('[data-read-more-btn]');
    if (!readMoreButton || !this.contains(readMoreButton)) return;

    const wrapper = readMoreButton.closest('[data-read-more-wrapper]');
    if (!(wrapper instanceof HTMLElement)) return;

    event.preventDefault();
    wrapper.classList.add('is-expanded');
  };

  async #handleBudgetPayClick(button) {
    const toggleHost = button.closest('[data-budget-pay-toggle]');
    const root = button.closest('.add-to-bag-modal__product-information');
    if (!toggleHost || !root || button.disabled) return;

    const form =
      toggleHost.closest('[data-product-handle]')?.querySelector('form[action*="/cart/add"]') ??
      root.querySelector('form[action*="/cart/add"]');
    if (!form) {
      console.error('[AddToBagModal] Budget Pay product form was not found');
      return;
    }

    if (toggleHost.hasAttribute('data-active')) {
      toggleHost.removeAttribute('data-active');
      button.setAttribute('aria-pressed', 'false');
      form.querySelector('input[name="selling_plan"]')?.remove();
      return;
    }

    button.disabled = true;
    let core;
    try {
      core = await import(this.#budgetPayCoreSource);
      const productRoot = toggleHost.closest('[data-product-handle]');
      const productHandle = productRoot?.dataset.productHandle ?? '';
      const minInstallments = Number(toggleHost.dataset.minInstallments ?? 0);
      const priceCents = Number(toggleHost.dataset.productPriceCents ?? 0);

      await core.syncBudgetPayStateWithContext({
        toggleEl: toggleHost,
        domRoot: root,
        getProductHandle: () => productHandle,
        getForm: () => form,
        getMinInstallments: () => minInstallments,
        getSelectedQuantity: () => Number(form.elements.quantity?.value ?? 1),
        getProductPriceCents: () => priceCents,
        getEligibilityConfig: () => core.getEligibilityFromDataset(toggleHost.dataset),
        getSellingPlanInputId: () => `budget-pay-selling-plan-input-${this.#openRequestId}`,
      });

      toggleHost.setAttribute('data-active', '');
      button.setAttribute('aria-pressed', 'true');
    } catch (error) {
      if (core) core.showBudgetPayError(error.message, toggleHost);
      console.error('[AddToBagModal] Budget Pay click failed', error);
    } finally {
      button.disabled = false;
    }
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    if (!source) return;

    const currentGallery = this.querySelector('.add-to-bag-modal__product-information media-gallery');
    const nextGallery = source.querySelector('.product-information media-gallery, media-gallery');
    if (!currentGallery || !nextGallery) return;

    this.#configureGallery(nextGallery);
    morph(currentGallery, nextGallery);
    this.#configureGallery(currentGallery);

    requestAnimationFrame(() => {
      const productInformation = this.querySelector('.add-to-bag-modal__product-information');
      if (productInformation) {
        this.#activateModalApps(productInformation);
        this.#matchClearpayPdpLayout(productInformation);
        this.#reserveStickySpace();
      }
    });
  };

  async openWithProduct(productUrl) {
    const content = document.getElementById(MODAL_CONTENT_ID);
    if (!content) return;

    const requestId = ++this.#openRequestId;
    this.#showSkeleton(content);

    // Finish closing the mini cart before opening another modal backdrop.
    if (!this.#cartClosePromise) {
      const drawer = Array.from(document.querySelectorAll('cart-drawer-component')).find(
        (candidate) => candidate.querySelector('dialog')?.open
      );
      if (drawer) {
        this.#returnCartDrawer = drawer;
        this.#cartClosePromise = drawer.closeDialog().finally(() => {
          this.#cartClosePromise = null;
        });
      }
    }
    await this.#cartClosePromise;
    if (requestId !== this.#openRequestId || !this.isConnected) return;

    if (this.refs.dialog && !this.refs.dialog.open) {
      this.showDialog();
    }

    const productInformation = await this.#getProductInformation(productUrl);
    if (requestId !== this.#openRequestId) return;

    content.removeAttribute('aria-busy');
    if (!productInformation) {
      content.textContent = 'Product information could not be loaded.';
      return;
    }

    const nextContent = document.createElement('div');
    nextContent.appendChild(productInformation);
    // Clear the skeleton first so morph builds the product markup fresh instead of diffing against it.
    content.replaceChildren();

    // Rendering failures have to stay inside the drawer. The loader falls back to
    // the product page when the modal cannot open, and that fallback must not
    // fire once the shopper is already looking at the popup.
    this.#runSetupStep(() => morph(content, nextContent));
    this.#runSetupStep(() => this.#namespaceIds(content));
    this.#runSetupStep(() => {
      if (
        this.refs.dialog.classList.contains('add-to-bag-modal__dialog--centered') &&
        window.matchMedia('(min-width: 768px)').matches
      ) {
        const highlights = content.querySelector('.product-description-block')?.closest('details.product-accordion');
        if (highlights) highlights.open = true;
      }
    });
    this.#runSetupStep(() => this.#addViewDetailsLink(content, productUrl));
    this.#runSetupStep(() => this.#arrangeProductSummary(content));
    this.#runSetupStep(() => this.#addQuantityLabel(content));
    this.#runSetupStep(() => this.#configureGallery(content));
    this.#runSetupStep(() => this.#activateModalApps(content));
    this.#runSetupStep(() => this.#matchClearpayPdpLayout(content));
    this.#runSetupStep(() => this.#reserveStickySpace());

    if (!content.childElementCount) content.textContent = 'Product information could not be loaded.';
  }

  #showSkeleton(content) {
    content.setAttribute('aria-busy', 'true');
    content.innerHTML = `
      <div class="add-to-bag-modal__skeleton" role="status">
        <span class="visually-hidden">Loading product details</span>
        <div class="add-to-bag-modal__skeleton-media" aria-hidden="true">
          <div class="add-to-bag-modal__skeleton-block add-to-bag-modal__skeleton-image"></div>
          <div class="add-to-bag-modal__skeleton-thumbs">
            ${'<div class="add-to-bag-modal__skeleton-block"></div>'.repeat(5)}
          </div>
        </div>
        <div class="add-to-bag-modal__skeleton-details" aria-hidden="true">
          <div class="add-to-bag-modal__skeleton-block add-to-bag-modal__skeleton-line"></div>
          <div class="add-to-bag-modal__skeleton-block add-to-bag-modal__skeleton-line add-to-bag-modal__skeleton-line--short"></div>
          <div class="add-to-bag-modal__skeleton-block add-to-bag-modal__skeleton-price"></div>
          <div class="add-to-bag-modal__skeleton-block add-to-bag-modal__skeleton-line add-to-bag-modal__skeleton-line--medium"></div>
          <div class="add-to-bag-modal__skeleton-block add-to-bag-modal__skeleton-line add-to-bag-modal__skeleton-line--label"></div>
          <div class="add-to-bag-modal__skeleton-options">
            ${'<div class="add-to-bag-modal__skeleton-block"></div>'.repeat(5)}
          </div>
        </div>
      </div>`;
  }

  #runSetupStep(step) {
    try {
      step();
    } catch (error) {
      console.error('[AddToBagModal] Popup setup step failed', error);
    }
  }

  #matchClearpayPdpLayout(root) {
    const apply = () => {
      root.querySelectorAll('square-placement').forEach((placement) => {
        const shadowRoot = placement.shadowRoot;
        if (!shadowRoot || shadowRoot.querySelector('[data-add-to-bag-clearpay-layout]')) return;

        const style = document.createElement('style');
        style.setAttribute('data-add-to-bag-clearpay-layout', '');
        style.textContent = `
          :host {
            display: block !important;
            height: fit-content !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .afterpay-paragraph {
            height: auto !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        `;
        shadowRoot.appendChild(style);
      });
    };

    // The app attaches its shadow root asynchronously after the fetched product
    // markup is connected. Re-apply briefly to cover that initialization and
    // any immediate app render without changing how its script is loaded.
    apply();
    requestAnimationFrame(apply);
    window.setTimeout(apply, 250);
    window.setTimeout(apply, 1000);
  }

  #addQuantityLabel(content) {
    const quantityRow = content.querySelector('.quantity-row');
    const input = quantityRow?.querySelector('input[name="quantity"]');
    if (!quantityRow || !(input instanceof HTMLInputElement) || quantityRow.closest('.add-to-bag-modal__quantity-group')) return;

    const group = document.createElement('div');
    group.className = 'add-to-bag-modal__quantity-group';
    const label = document.createElement('div');
    label.className = 'add-to-bag-modal__quantity-label';
    label.innerHTML = `Quantity: <span data-modal-quantity-value>${input.value}</span>`;
    quantityRow.before(group);
    group.append(label, quantityRow);
  }

  #reserveStickySpace() {
    const content = document.getElementById(MODAL_CONTENT_ID);
    if (!content) return;

    this.#stickyResizeObserver?.disconnect();
    const stickyElements = Array.from(
      content.querySelectorAll(
        '.buy-buttons-container, .product-notify-me--standalone-visible, .shopify-app-block:has([id*="notify_me"], [id*="notify-me"], [data-notify-me])'
      )
    );

    const update = () => {
      const stickyHeight = stickyElements.reduce((height, element) => {
        if (!(element instanceof HTMLElement) || getComputedStyle(element).display === 'none') return height;
        return Math.max(height, element.getBoundingClientRect().height);
      }, 0);

      content.style.paddingBottom = `${Math.max(84, Math.ceil(stickyHeight) + 12)}px`;
      content.style.setProperty('--add-to-bag-modal-purchase-space', `${Math.ceil(stickyHeight)}px`);
    };

    this.#stickyResizeObserver = new ResizeObserver(update);
    stickyElements.forEach((element) => this.#stickyResizeObserver?.observe(element));
    update();
  }

  #arrangeProductSummary(root) {
    const title = root.querySelector('product-title.product-title-wrapper, .product-title-block');
    const rating = root.querySelector('.review-block.rating-wrapper');
    if (!title || !rating) return;

    // Keep the wishlist app adjacent to the title so the PDP title-row styling
    // also applies inside the modal, then position reviews beneath that row.
    const wishlist = title.nextElementSibling?.matches('.shopify-app-block')
      ? title.nextElementSibling
      : null;
    const summaryEnd = wishlist ?? title;
    if (summaryEnd.nextElementSibling !== rating) {
      summaryEnd.after(rating);
    }
  }

  #activateModalApps(root) {
    const appSelector = [
      '[id*="notify_me"]',
      '[id*="notify-me"]',
      '[data-notify-me]',
      '[id*="budget_pay"]',
      '[id*="budget-pay"]',
      '[data-budget-pay-product-installments]',
      '[data-budget-pay-toggle]',
      'budget-pay-product-installments',
    ].join(',');
    const appElements = root.querySelectorAll(appSelector);
    const budgetAssetScripts = root.querySelectorAll(
      'script[src*="budget-pay"], script[src*="budget_pay"]'
    );
    const budgetCartSource = this.#budgetPayCartSource(root, budgetAssetScripts);
    if (budgetCartSource) {
      const coreSource = new URL(budgetCartSource, window.location.href);
      coreSource.pathname = coreSource.pathname.replace(/budget-pay-cart\.js$/i, 'bp-cart-core.js');
      this.#budgetPayCoreSource = coreSource.toString();
    }
    if (!appElements.length && !budgetAssetScripts.length) return;

    const scripts = new Set();
    const budgetPayScripts = new Set();
    appElements.forEach((element) => {
      const appBlock = element.closest('.shopify-app-block, .out-of-stock-block') ?? element;
      appBlock.querySelectorAll('script').forEach((script) => {
        scripts.add(script);
        if (
          element.matches(
            '[id*="budget_pay"], [id*="budget-pay"], [data-budget-pay-product-installments], [data-budget-pay-toggle], budget-pay-product-installments'
          )
        ) {
          budgetPayScripts.add(script);
        }
      });
    });
    root
      .querySelectorAll('script[data-add-to-bag-product-app-asset]')
      .forEach((script) => scripts.add(script));
    budgetAssetScripts.forEach((script) => {
      scripts.add(script);
      budgetPayScripts.add(script);
    });

    const initializeBudgetPay = () => {
      const toggleHosts = root.querySelectorAll(
        '[data-budget-pay-product-installments], [data-budget-pay-toggle]'
      );
      if (!toggleHosts.length) return;

      // Budget Pay's PDP module listens for Shopify's section lifecycle event
      // and uses it to bind click handlers to product markup added after load.
      // Re-importing the module does not reliably initialize modal content.
      document.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          detail: { sectionId: root.closest('.shopify-section')?.id ?? MODAL_CONTENT_ID },
        })
      );

      requestAnimationFrame(() => this.#bindBudgetPayFallback(root, budgetAssetScripts));
    };

    scripts.forEach((script) => {
      if (script.hasAttribute('data-add-to-bag-product-app-activated')) return;
      const isBudgetPayScript =
        budgetPayScripts.has(script) || Boolean(script.src && /budget[-_ ]?pay/i.test(script.src));
      const modalForm = root.querySelector('product-form-component form');
      const modalToggle = modalForm && window.BudgetPayCart?.getToggleForForm?.(modalForm);
      if (isBudgetPayScript && modalToggle) {
        script.setAttribute('data-add-to-bag-product-app-activated', '');
        return;
      }

      const liveScript = document.createElement('script');
      Array.from(script.attributes).forEach(({ name, value }) => liveScript.setAttribute(name, value));
      liveScript.setAttribute('data-add-to-bag-product-app-activated', '');
      liveScript.textContent = script.textContent;
      if (isBudgetPayScript) {
        // The app block uses document.write() to emit its module during the
        // initial page parse. Extract that URL because document.write cannot
        // bootstrap a component inserted later inside the modal.
        const embeddedSource = script.textContent.match(
          /src=["']([^"']*budget[-_ ]?pay[^"']*\.js[^"']*)["']/i
        )?.[1];
        const budgetPaySource = liveScript.src || embeddedSource?.replaceAll('&amp;', '&');

        if (budgetPaySource) {
          liveScript.type = 'module';
          liveScript.textContent = '';
          liveScript.src = new URL(budgetPaySource, window.location.href).toString();
          liveScript.addEventListener('load', initializeBudgetPay, { once: true });
          liveScript.addEventListener(
            'error',
            () => console.error('[AddToBagModal] Could not load Budget Pay', liveScript.src),
            { once: true }
          );
        }
      }
      script.replaceWith(liveScript);
    });

    requestAnimationFrame(initializeBudgetPay);
  }

  /**
   * Address of Budget Pay's budget-pay-cart.js for the product content in root, from which the
   * address of its bp-cart-core.js module is built. Today the Product Installments app block prints
   * that script's tag, and the tag is where the address is read. If the block loads its scripts
   * after the page instead, it prints no tag; it can then print the same address on any element of
   * the block as data-budget-pay-cart-src="{{ 'budget-pay-cart.js' | asset_url }}". That attribute
   * is read first. With neither, the caller keeps BUDGET_PAY_CORE_FALLBACK or does not bind, as
   * before.
   * @param {Element} root
   * @param {NodeListOf<HTMLScriptElement>} budgetAssetScripts
   * @returns {string} The address, or an empty string.
   */
  #budgetPayCartSource(root, budgetAssetScripts) {
    const declared = root
      .querySelector('[data-budget-pay-cart-src]')
      ?.getAttribute('data-budget-pay-cart-src');
    if (declared && /budget-pay-cart\.js/i.test(declared)) return declared;
    const cartScript = Array.from(budgetAssetScripts).find((script) =>
      /budget-pay-cart\.js/i.test(script.src)
    );
    return cartScript?.src ?? '';
  }

  #bindBudgetPayFallback(root, budgetAssetScripts) {
    root.querySelectorAll('[data-budget-pay-toggle]').forEach((toggleHost) => {
      if (toggleHost._budgetPayToggleInstance) return;
      if (toggleHost.hasAttribute('data-add-to-bag-budget-pay-bound')) return;

      const button = toggleHost.querySelector(
        '[data-budget-pay-toggle] button, .budget-pay-toggle__button, button'
      );
      if (!button) return;

      const cartSource = this.#budgetPayCartSource(root, budgetAssetScripts);
      if (!cartSource) return;

      const coreSource = new URL(cartSource, window.location.href);
      coreSource.pathname = coreSource.pathname.replace(/budget-pay-cart\.js$/i, 'bp-cart-core.js');
      const corePromise = import(coreSource.toString());

      toggleHost.setAttribute('data-add-to-bag-budget-pay-bound', '');
      button.addEventListener('click', async (event) => {
        // If the app initialized after this fallback was bound, allow its own
        // click listener to be the sole controller.
        if (toggleHost._budgetPayToggleInstance) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const form =
          toggleHost.closest('[data-product-handle]')?.querySelector('form[action*="/cart/add"]') ??
          root.querySelector('form[action*="/cart/add"]');
        if (!form) return;

        if (toggleHost.hasAttribute('data-active')) {
          toggleHost.removeAttribute('data-active');
          button.setAttribute('aria-pressed', 'false');
          form.querySelector('input[name="selling_plan"]')?.remove();
          return;
        }

        button.disabled = true;
        let core;
        try {
          core = await corePromise;
          const productRoot = toggleHost.closest('[data-product-handle]');
          const productHandle = productRoot?.dataset.productHandle ?? '';
          const minInstallments = Number(toggleHost.dataset.minInstallments ?? 0);
          const priceCents = Number(toggleHost.dataset.productPriceCents ?? 0);

          await core.syncBudgetPayStateWithContext({
            toggleEl: toggleHost,
            domRoot: root,
            getProductHandle: () => productHandle,
            getForm: () => form,
            getMinInstallments: () => minInstallments,
            getSelectedQuantity: () => Number(form.elements.quantity?.value ?? 1),
            getProductPriceCents: () => priceCents,
            getEligibilityConfig: () => core.getEligibilityFromDataset(toggleHost.dataset),
            getSellingPlanInputId: () => `budget-pay-selling-plan-input-${this.#openRequestId}`,
          });

          toggleHost.setAttribute('data-active', '');
          button.setAttribute('aria-pressed', 'true');
        } catch (error) {
          if (core) core.showBudgetPayError(error.message, toggleHost);
          console.error('[AddToBagModal] Budget Pay fallback failed', error);
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  #configureGallery(root) {
    root.querySelectorAll('media-gallery slideshow-component').forEach((slideshow) => {
      slideshow.removeAttribute('infinite');
      slideshow.setAttribute('initial-slide', '0');

      // Refresh the controls after disabling looping so the previous arrow is
      // disabled (and hidden) at the first slide.
      if ('current' in slideshow) {
        if (slideshow.current !== 0 && typeof slideshow.select === 'function') {
          slideshow.select(0, undefined, { animate: false });
        } else {
          slideshow.current = slideshow.current;
        }
      }
    });
  }

  #addViewDetailsLink(root, productUrl) {
    const productUrlObject = new URL(productUrl, window.location.href);
    const details = root.querySelector('.product-information__details');
    if (!details || details.querySelector('.add-to-bag-modal__view-details')) return;

    const link = document.createElement('a');
    link.className = 'add-to-bag-modal__view-details';
    link.href = productUrlObject.pathname;
    link.textContent = 'View Full Details';
    const accordionGroups = details.querySelectorAll('.product-accordion-group');
    const lastAccordionGroup = accordionGroups[accordionGroups.length - 1];
    if (lastAccordionGroup) {
      lastAccordionGroup.after(link);
    } else {
      details.append(link);
    }
  }

  #namespaceIds(root) {
    const idMap = new Map();
    const prefix = `add-to-bag-${this.#openRequestId}`;
    const externalIds = new Set(
      Array.from(document.querySelectorAll('[id]'))
        .filter((element) => !root.contains(element))
        .map((element) => element.id)
    );

    root.querySelectorAll('[id]').forEach((element) => {
      if (!element.id || element.id.startsWith(`${prefix}-`)) return;

      // Preserve server-rendered IDs unless this page already contains the
      // same ID outside the modal. Product apps use those original IDs to
      // associate late-inserted controls with their product form.
      // The section wrapper is always renamed: the copied section <style> scopes
      // the PDP desktop layout (sticky, fit-content height) to that ID, which would
      // break the modal's scroll column on pages without a colliding PDP section.
      const isSectionWrapper = element.matches(PRODUCT_INFORMATION_SELECTOR);
      if (!isSectionWrapper && !externalIds.has(element.id)) return;

      const nextId = `${prefix}-${element.id}`;
      idMap.set(element.id, nextId);
      element.id = nextId;
    });

    if (!idMap.size) return;

    const directAttributes = [
      'for',
      'form',
      'form-id',
      'form_id',
      'list',
      'data-form-id',
      'data-form_id',
      'data-product-form-id',
      'data-product-form_id',
    ];
    const tokenAttributes = ['aria-controls', 'aria-describedby', 'aria-labelledby', 'aria-owns'];

    root.querySelectorAll('*').forEach((element) => {
      directAttributes.forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (value && idMap.has(value)) {
          element.setAttribute(attribute, idMap.get(value));
        }
      });

      tokenAttributes.forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value) return;

        const nextValue = value
          .split(/\s+/)
          .map((token) => idMap.get(token) || token)
          .join(' ');

        if (nextValue !== value) {
          element.setAttribute(attribute, nextValue);
        }
      });

      const href = element.getAttribute('href');
      const targetId = href?.startsWith('#') ? href.slice(1) : '';
      if (targetId && idMap.has(targetId)) {
        element.setAttribute('href', `#${idMap.get(targetId)}`);
      }
    });
  }

  #observeTriggers() {
    if (!('IntersectionObserver' in window)) return;

    this.#prefetchObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) return;

          this.#prefetch(entry.target.dataset.productUrl);
          this.#prefetchObserver?.unobserve(entry.target);
        });
      },
      { rootMargin: '800px 0px' }
    );

    document.querySelectorAll(OPEN_SELECTOR).forEach((trigger) => {
      this.#prefetchObserver?.observe(trigger);
    });

    this.#triggerObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.matches(OPEN_SELECTOR)) {
            this.#prefetchObserver?.observe(node);
          }

          node.querySelectorAll(OPEN_SELECTOR).forEach((trigger) => {
            this.#prefetchObserver?.observe(trigger);
          });
        });
      });
    });

    this.#triggerObserver.observe(document.body, { childList: true, subtree: true });
  }

  #prefetch(productUrl) {
    this.#getProductInformation(productUrl);
  }

  async #getProductInformation(productUrl) {
    const cacheKey = this.#normalizeProductUrl(productUrl);
    if (!cacheKey) return null;

    if (!this.#cache.has(cacheKey)) {
      this.#cache.set(cacheKey, this.#loadProductInformation(cacheKey));
    }

    let productInformation;
    try {
      productInformation = await this.#cache.get(cacheKey);
    } catch (error) {
      // A rejected promise must never stay cached, or a failed prefetch would
      // replay itself on every later click of the same tile.
      this.#cache.delete(cacheKey);
      console.error('[AddToBagModal] Product information could not be loaded', error);
      return null;
    }

    if (!productInformation) {
      this.#cache.delete(cacheKey);
      return null;
    }

    return productInformation.cloneNode(true);
  }

  async #loadProductInformation(productUrl) {
    const html = await this.#fetchProductPage(productUrl);
    const productInformation = html?.querySelector(PRODUCT_INFORMATION_SELECTOR);
    if (!productInformation) return null;

    // The fetched document is still detached here, so remove looping before
    // its slideshow custom elements are connected and initialise.
    this.#configureGallery(productInformation);

    const wrapper = document.createElement('div');
    wrapper.className = 'add-to-bag-modal__product-information';
    wrapper.setAttribute('data-add-to-bag-modal-product-information', '');

    // App extension JavaScript can be emitted in the product page head rather
    // than inside its app block. Carry the modal's product-app assets with it.
    html.querySelectorAll('script[src]').forEach((script) => {
      const source = script.getAttribute('src') ?? '';
      if (!/(?:notify[-_ ]?me|tjc[-_ ]?notify|budget[-_ ]?pay)/i.test(source)) return;

      const appScript = script.cloneNode(true);
      appScript.setAttribute('data-add-to-bag-product-app-asset', '');
      wrapper.appendChild(appScript);
    });

    html.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
      const href = link.getAttribute('href') ?? '';
      if (/(?:notify[-_ ]?me|tjc[-_ ]?notify|budget[-_ ]?pay)/i.test(href)) {
        wrapper.appendChild(link.cloneNode(true));
      }
    });

    // The product section's component styles are rendered in a sibling <style>
    // element, outside .product-information. Product pages already have those
    // rules in the document, but collection/search pages do not. Carry the
    // stylesheet into the drawer together with the fetched product markup.
    const productInformationStyle = Array.from(html.querySelectorAll('style')).find((style) =>
      style.textContent.includes(PRODUCT_INFORMATION_STYLE_MARKER)
    );
    if (productInformationStyle) {
      wrapper.appendChild(productInformationStyle.cloneNode(true));
    }

    wrapper.appendChild(productInformation.cloneNode(true));

    return wrapper;
  }

  #normalizeProductUrl(productUrl) {
    if (!productUrl) return '';

    const url = new URL(productUrl, window.location.href);
    const variantId = url.searchParams.get('variant');
    url.search = '';
    // SKU-based cards must load and cache the selected variant separately.
    if (variantId) url.searchParams.set('variant', variantId);
    url.hash = '';

    return url.toString();
  }

  async #fetchProductPage(productUrl) {
    const controller = new AbortController();
    this.#controllers.add(controller);

    const fetchInit = { signal: controller.signal };
    if ('priority' in Request.prototype) fetchInit.priority = 'low';

    try {
      const response = await fetch(productUrl, fetchInit);
      if (!response.ok) return null;

      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    } catch (error) {
      if (error?.name !== 'AbortError') console.error(error);
      return null;
    } finally {
      this.#controllers.delete(controller);
    }
  }
}

if (!customElements.get('add-to-bag-modal')) {
  customElements.define('add-to-bag-modal', AddToBagModal);
}
